import React, { useState } from "react";
import "../solutions/Solutions.css";

const FIBER_SHUFFLE_CONFIG = {
  hero: {
    backgroundImage:
      "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FAdobeStock_682811184%20(1).jpeg?alt=media&token=dbd6359b-e172-4b64-93b1-267017170417",

    heading: "MAXIMUM CAPACITY, ZERO COMPLEXITY",

    description:
      "Simplify high-density fiber connectivity with pre-engineered Fiber Shuffle solutions. Eliminate splicing, reduce optical loss, and accelerate deployment for AI data centers.",

    buttonLabel: "Talk to an Expert",

    buttonLink:
      "mailto:sales@gofoton.com?subject=I%E2%80%99d%20like%20to%20learn%20more%20about%20Fiber%20Shuffle%20solutions",
  },

  introduction: {
    heading: "Fiber Shuffles ease and speed time to service",

    description:
      "The PEACOC® Fiber Shuffle solutions help simplify physical layer connectivity for AI data centers and hyperscale companies where massive amounts of fiber are needed. By routing fibers from MPO inputs to MPO outputs, Shuffles eliminate the need for splice and patch panels and minimize optical loss, reduce field tech time, and save rack space.",

    listHeading: "Fiber Shuffle solutions are great to consider when:",

    bullets: [
      "There are massive amounts of fiber cabling taking up a lot of space",
      "You’ve experienced problems with installation errors, causing loss or misrouted connections",
      "Signal integrity is critical for your fiber plant, and it cannot tolerate losses",
      "There are no resources or time to splice",
      "There’s no rack space for patch panels",
      "You’re under pressure to turn up services as fast as possible",
    ],

    applications: [
      "AI data centers",
      "Hyperscale",
      "Cloud",
      "HPC clusters",
    ],
  },

  accordionSections: [
    {
      key: "howItWorks",
      title: "How Fiber Shuffles work",
      content: {
        type: "text",
        paragraphs: [
          "Inside a PEACOC Fiber Shuffle, fibers are organized to optimize their pathways from one network component to another. Pre-terminated fiber cables are connected to adapters in the shuffle, which then link to other network devices like servers, switches, and storage systems.",
          "By managing fiber pathways and reducing the number of connections and splices, Fiber Shuffles help minimize signal loss and ensure high-quality data transmission across the network.",
        ],
      },
    },

    {
      key: "comparison",
      title:
        "Considering a move from patch or splice? Here’s how shuffling compares.",
      content: {
        type: "comparison",
        heading: "Here’s how shuffling saves time, space, and cost.",

        cards: [
          {
            title: "Splice",
            image:
              "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FspliceCassettes.png?alt=media&token=301e9379-d206-494a-a5fd-fd63b37e9bf3",

            description:
              "Splicing takes time, skilled labor, and specific tools. Fiber routing is fixed, and there is no convenient test point if something goes wrong.",
          },

          {
            title: "Patch",
            image:
              "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2Fmodular.png?alt=media&token=f0ae20ad-e284-4475-8bb4-ec6d54f74ca5",

            description:
              "Fiber Shuffle solutions save rack space and technician time. For example, 1,152 fibers would otherwise require eight separate 144-fiber panels.",
          },
        ],
      },
    },

    {
      key: "closerLook",
      title:
        "A closer look at Go!Foton’s cassette and panel Fiber Shuffle solutions",
      content: {
        type: "text",
        paragraphs: [
          "Fiber Shuffles use a modular design, often in the form of cassettes or panels.",
          "Cassette solutions work well up to about 432 fibers. Beyond that density, panel solutions are recommended.",
        ],
      },
    },

    {
      key: "shuffleCassettes",
      title: "Shuffle Cassettes",
      content: {
        type: "imageText",

        paragraphs: [
          "Low-density applications can often be handled within a single PEACOC Fiber Shuffle Cassette or across the adjacent cassette.",
          "These schematics show a typical application where a pair of fibers from the MPO on the left is dropped off to each of the MPOs on the right.",
        ],

        image:
          "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FShuffle%20Cassettes%20Diagram.png?alt=media&token=2e3e2c8b-7ff0-4880-af50-7df1cf824d7c",

        imageAlt: "Fiber Shuffle cassette product and routing diagram",
      },
    },

    {
      key: "shufflePanels",
      title: "Shuffle Panels",
      content: {
        type: "imageText",

        paragraphs: [
          "PEACOC Fiber Shuffle Panels offer a highly manageable approach to route fiber connections from card-to-card or shelf-to-shelf. Go!Foton’s solution supports an industry-standout 2,304 fiber connections within a single rack unit panel. This sets a new standard in fiber optic connectivity.",
          "Catering to the dynamic requirements of modern digital infrastructures, the innovative architecture accommodates complex interconnect patterns with ease while affording the flexibility to shuffle, or route, fibers as needed in a craft-friendly network device.",
        ],

        image:
          "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FShuffle%20Panels%20Image.png?alt=media&token=214d7846-5bd0-49eb-8638-5fa02cfdf4ba",

        imageAlt: "High-density PEACOC Fiber Shuffle panel",
      },
    },

    {
      key: "whyChoose",
      title: "Why choose Go!Foton fiber shuffle solutions",
      content: {
        type: "text",

        paragraphs: [
          "As with other technology platforms, Go!Foton’s focus is on cascading innovation into solutions designed specifically for AI data centers.",
          "Both relatively low- and high-fiber-density shuffle solutions incorporate spreadable adapter technology and support conventional and VSFF adapters. These design features are integrated into cassettes and panels, allowing easy access to the connectors.",
          "The panels also feature enhanced cable management, particularly beneficial in fiber-dense applications.",
        ],
      },
    },
  ],

  featureSection: {
    heading: "Fiber Shuffle features",

    description:
      "Go!Foton Fiber Shuffle solutions incorporate proven PEACOC technology to improve access, simplify installation, manage high fiber counts, and help protect critical network connections.",

      cards: [
        {
          title: "Spreadable adapters",
          desc: "Improved port access",
          image:
            "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FSpreadable%20Adapters.jpg?alt=media&token=041b595c-75e5-424e-ad81-82ab55bb4eef",
        },
        {
          title: "Modular",
          desc: "Scale & upgrade easily",
          image:
            "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FModular.jpg?alt=media&token=3eccbe21-20fa-40ea-968a-577e4964e2c5",
        },
        {
          title: "Sliding cassettes",
          desc: "Front & rear access",
          image:
            "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FSliding%20Cassette.jpg?alt=media&token=51f33447-6882-4728-94aa-a1e9451401e3",
        },
        {
          title: "Cable management",
          desc: "Radius limiters & routing guides",
          image:
            "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FCable%20Management.jpg?alt=media&token=8b1c78d5-cc77-4ff0-b192-d11cb4e04aec",
        },
      ],
  },

  faq: [
    {
      question: "What is fiber shuffling?",
      answer:
        "Shuffling regroups and evenly redistributes fiber connections to minimize fiber optic connectivity complexity and maximize data throughput.",
    },

    {
      question: "What are the benefits of using a shuffle solution?",
      answer:
        "Fiber Shuffle solutions help accommodate expanding GPU clusters to boost bandwidth and reduce network latency, while also speeding service turn-up and mitigating installation-error risk.",
    },

    {
      question: "How do I justify the cost of a shuffle solution?",
      answer:
        "Using PEACOC Fiber Shuffle eliminates the need for splicing labor and patch panels.",
    },

    {
      question: "What is the typical lead time?",
      answer:
        "Standard lead time is typically three to four weeks after receipt of order.",
    },

    {
      question: "What is the product warranty?",
      answer:
        "Go!Foton warrants to its customer that its products shall be free from defects in material and workmanship for a period of one year from the date of shipment.",
    },

    {
      question: "What are the standards and compliance certifications?",
      answer:
        "The primary standards for compliance testing include Telcordia GR-449, GR-409, GR-63, NEBS Level 3, and UL, where applicable.",
    },

    {
      question:
        "How do Fiber Shuffle solutions save time and ensure error-free installations?",
      answer:
        "PEACOC Fiber Shuffle solutions organize and route fiber connections within a cassette or panel to ensure precise fiber optic mapping for high-density data center architectures. These pre-configured solutions streamline deployments in large installations.",
    },

    {
      question: "Are Fiber Shuffle solutions green?",
      answer:
        "These systems are passive and do not require power. They also minimize Layer 1 rack-space requirements, creating more space for critical power and cooling systems.",
    },
  ],

  footer: {
    heading: "Ready to simplify high-density fiber connectivity?",

    description:
      "Our experts can help design a Fiber Shuffle solution around your network architecture and fiber-density requirements.",

    buttonLabel: "Talk to an Expert",

    buttonLink:
      "mailto:sales@gofoton.com?subject=I%E2%80%99d%20like%20to%20learn%20more%20about%20Fiber%20Shuffle%20solutions",
  },
};

const formatRegisteredMarks = (text) => {
  if (typeof text !== "string") return text;

  return text.split("®").map((part, index, array) => (
    <React.Fragment key={index}>
      {part}
      {index < array.length - 1 && (
        <sup className="registered-mark">®</sup>
      )}
    </React.Fragment>
  ));
};

function FiberShuffle() {
    const [expandedSections, setExpandedSections] = useState(() =>
      Object.fromEntries(
        FIBER_SHUFFLE_CONFIG.accordionSections.map((section) => [
          section.key,
          true,
        ])
      )
    );
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleSection = (sectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const toggleIndex = (index) => {
    setActiveIndex((current) => (current === index ? null : index));
  };

  const renderAccordionContent = (content) => {
    if (content.type === "text") {
      return (
        <div className="fiber-shuffle-accordion-text">
          {content.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      );
    }

    if (content.type === "imageText") {
      return (
        <div className="fiber-shuffle-image-text">
          <div className="fiber-shuffle-image-text-copy">
            {content.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="fiber-shuffle-tab-image-wrapper">
            <img src={content.image} alt={content.imageAlt} />
          </div>
        </div>
      );
    }

    if (content.type === "comparison") {
      return (
        <div className="fiber-shuffle-comparison">
          <h3>{content.heading}</h3>

          <div className="fiber-shuffle-comparison-grid">
            {content.cards.map((card) => (
              <article
                className="fiber-shuffle-comparison-card"
                key={card.title}
              >
                <img src={card.image} alt={card.title} />

                <div className="fiber-shuffle-comparison-copy">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="contact-page fiber-shuffle-page">
      {/* EXISTING SITE MARQUEE STRUCTURE */}

      <div className="single-marquee">
        <div className="single-marquee-slide">
          <video
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            src=""
            poster={FIBER_SHUFFLE_CONFIG.hero.backgroundImage}
            autoPlay
            loop
            muted
            playsInline
          />

          <div className="single-overlay-content fiber-shuffle-hero-content">
            <h1>{FIBER_SHUFFLE_CONFIG.hero.heading}</h1>

            <p>{FIBER_SHUFFLE_CONFIG.hero.description}</p>

            <a
              href={FIBER_SHUFFLE_CONFIG.hero.buttonLink}
              className="cta-button"
            >
              {FIBER_SHUFFLE_CONFIG.hero.buttonLabel}
            </a>
          </div>
        </div>
      </div>

      {/* EXISTING CURVED DIVIDER */}

      <svg
        className="section-divider"
        viewBox="0 0 1440 150"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient
            id="fiberShuffleWhiteSplit"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="50%"
              stopColor="white"
              stopOpacity="1"
            />
          </linearGradient>
        </defs>

        <path
          d="
            M0,150
            H1440
            V0
            Q720,75 0,0
            Z
          "
          fill="url(#fiberShuffleWhiteSplit)"
        />
      </svg>

      {/* INTRODUCTION */}

      <section className="fiber-shuffle-introduction">
        <aside className="fiber-shuffle-applications">
          <h2>Applications</h2>

          <ul>
            {FIBER_SHUFFLE_CONFIG.introduction.applications.map(
              (application) => (
                <li key={application}>{application}</li>
              )
            )}
          </ul>
        </aside>

        <div className="fiber-shuffle-introduction-copy">
          <h2>{FIBER_SHUFFLE_CONFIG.introduction.heading}</h2>

          <p>
            {formatRegisteredMarks(
              FIBER_SHUFFLE_CONFIG.introduction.description
            )}
          </p>

          <h3>{FIBER_SHUFFLE_CONFIG.introduction.listHeading}</h3>

          <ul>
            {FIBER_SHUFFLE_CONFIG.introduction.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTENT ACCORDIONS */}

      <section className="fiber-shuffle-tabs-section">
        <div className="product-accordion">
          {FIBER_SHUFFLE_CONFIG.accordionSections.map((section) => (
            <div className="accordion-block" key={section.key}>
              <button
                type="button"
                className="accordion-header"
                onClick={() => toggleSection(section.key)}
                aria-expanded={Boolean(expandedSections[section.key])}
              >
                {section.title}

                <span className="accordion-icon">
                  {expandedSections[section.key] ? "–" : "+"}
                </span>
              </button>

              {expandedSections[section.key] && (
                <div className="accordion-body">
                  {renderAccordionContent(section.content)}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURE CARDS */}

        <section className="fp-features-section fiber-shuffle-features">
        <h2 className="solutions-section-centeredText-subheading">
            {FIBER_SHUFFLE_CONFIG.featureSection.heading}
        </h2>

        <p className="solution-section-centeredText-description">
            {FIBER_SHUFFLE_CONFIG.featureSection.description}
        </p>

        <div className="fp-features-grid">
          {FIBER_SHUFFLE_CONFIG.featureSection.cards.map((card, index) => (
            <div className="fp-feature-card" key={index}>
              <img src={card.image} alt={card.title} />

              <div className="fp-feature-caption">
                <strong>{card.title}:</strong>
                <span>{card.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — EXISTING SITE STRUCTURE */}

      <div className="solutions-section-centeredText">
        <div className="solutions-section-centeredText-text-container">
          <h2
            className="solutions-section-centeredText-subheading"
            style={{ textAlign: "center" }}
          >
            Frequently Asked Questions
          </h2>
        </div>

        <div className="qa-accordion">
          {FIBER_SHUFFLE_CONFIG.faq.map((item, index) => (
            <div key={index} className="accordion-item">
              <div
                className="accordion-question"
                onClick={() => toggleIndex(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    toggleIndex(index);
                  }
                }}
              >
                <span style={{ fontSize: 20 }}>{item.question}</span>

                <span className="icon">
                  {activeIndex === index ? "−" : "+"}
                </span>
              </div>

              {activeIndex === index && (
                <div className="accordion-answer">
                  <p style={{ fontSize: 20, textAlign: "left" }}>
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}

      <div className="curved-footer-solutions">
        <div
          className="curved-top-overlay-solutions"
          style={{ backgroundColor: "white" }}
        />

        <h2>{FIBER_SHUFFLE_CONFIG.footer.heading}</h2>

        <p>{FIBER_SHUFFLE_CONFIG.footer.description}</p>

        <a
          className="connect-button"
          href={FIBER_SHUFFLE_CONFIG.footer.buttonLink}
        >
          {FIBER_SHUFFLE_CONFIG.footer.buttonLabel}
        </a>
      </div>
    </div>
  );
}

export default FiberShuffle;